import { QueryReturnTypeAll, QueryReturnTypeOptional } from '../../query';
import { Column } from '../../../columns/column';
import { PickQueryReturnType } from '../../pick-query-types';
import { RecordUnknown } from '../../../utils';
import { _clone } from '../../basic-features/clone/clone';
import { pushQueryValueImmutable } from '../../query-data';
import { QueryThen } from '../../then/then';

export class QueryMap {
  /**
   * Use `map` to transform individual records of a query result.
   *
   * Use `map` to transform individual records of a query result. If the query returns multiple, `map` function going to transform records one by one.
   *
   * For an optional query result (`findOptional`, `getOptional`, etc.), `map` is **not** called for empty results.
   *
   * For transforming the result of a query as a whole, consider using {@link Query.transform} instead.
   *
   * ```ts
   * // add a `titleLength` to every post
   * const posts = await db.post.limit(10).map((post, i) => ({
   *   ...post,
   *   titleLength: post.title.length,
   * }));
   *
   * posts[0].titleLength; // number
   *
   * // using the exact same `map` function to transform a single post
   * const singlePost = await db.post.find(id).map((post, i) => ({
   *   ...post,
   *   titleLength: post.title.length,
   * }));
   *
   * singlePost.titleLength; // number
   *
   * // can be used in sub-queries
   * const postsWithComments = await db.post.select('title', {
   *   comments: (q) =>
   *     q.comments.map((comment, i) => ({
   *       ...comment,
   *       truncatedContent: comment.content.slice(0, 100),
   *     })),
   * });
   *
   * postsWithComments[0].comments[0].truncatedContent; // string
   * ```
   *
   * @param fn - function to transform an individual record
   * @param thisArg - same as in the native array map
   */
  map<T extends PickQueryReturnType, Result>(
    this: T,
    fn: // `| null` is the case of aggregations such as `sum`.
    T extends {
      returnType: 'valueOrThrow';
      then: QueryThen<(infer Data) | null>;
    }
      ? (input: Data, index: number, value: Data) => Result
      : T['returnType'] extends QueryReturnTypeAll | 'pluck'
        ? T extends { then: QueryThen<(infer Data)[]> }
          ? (input: Data, index: number, arr: Data[]) => Result
          : never
        : // `| undefined` is needed to remove undefined type from map's arg
          T extends { then: QueryThen<(infer Data) | undefined> }
          ? (input: Data, index: number, value: Data) => Result
          : never,
    thisArg?: unknown,
  ): // When the map returns object, a query result is a map of key-value columns.
  // It's used to correctly infer type in case of a nested sub-query select with the map inside.
  Result extends RecordUnknown
    ? {
        [K in keyof T]: K extends 'result'
          ? { [K in keyof Result]: Column.Pick.QueryColumnOfType<Result[K]> }
          : K extends 'then'
            ? QueryThen<
                T['returnType'] extends QueryReturnTypeAll | 'pluck'
                  ? Result[]
                  : T['returnType'] extends QueryReturnTypeOptional
                    ? Result | undefined
                    : Result
              >
            : T[K];
      }
    : // When the map returns a scalar value, a query type should adjust to a single value
      {
        [K in keyof T]: K extends 'returnType'
          ? T['returnType'] extends QueryReturnTypeAll | 'pluck'
            ? 'pluck'
            : T['returnType'] extends 'one'
              ? 'value'
              : 'valueOrThrow'
          : K extends 'result'
            ? T['returnType'] extends QueryReturnTypeAll | 'pluck'
              ? { pluck: Column.Pick.QueryColumnOfType<Result> }
              : T['returnType'] extends QueryReturnTypeOptional
                ? { value: Column.Pick.QueryColumnOfType<Result | undefined> }
                : {
                    value: Column.Pick.QueryColumnOfType<
                      T extends {
                        returnType: 'valueOrThrow';
                        then: QueryThen<unknown | null>;
                      }
                        ? Result | null // aggregation such as `sum`
                        : Result
                    >;
                  }
            : K extends 'then'
              ? QueryThen<
                  T['returnType'] extends QueryReturnTypeAll | 'pluck'
                    ? Result[]
                    : T['returnType'] extends QueryReturnTypeOptional
                      ? Result | undefined
                      : T extends {
                            returnType: 'valueOrThrow';
                            then: QueryThen<unknown | null>;
                          }
                        ? Result | null
                        : Result
                >
              : T[K];
      } {
    return pushQueryValueImmutable(_clone(this), 'transform', {
      map: fn,
      thisArg,
    }) as never;
  }
}
